import { useSelector } from 'react-redux';
import { hasPermission } from '../config/rbac';

// One place for a component to ask "may this user do X" and "does this tenant
// have Y", so nothing has to restate a role list inline.
//
// The frontend was offering controls the backend then refused: a
// claimsManager saw "+ Add Provider" (riskCarriers:write is ORG_ADMINS +
// CLAIMS_STAFF-less), Team Management showed per-row reset actions
// (users:update is ORG_ADMINS), and a tenant with bordereaux switched off
// still got a "Bordereaux History" button whose endpoint answers 403.
//
// The server was always right. What was wrong was advertising a door and
// letting the user discover it is locked. These hooks read the SAME matrix
// (config/rbac.js) and the SAME tenant config the guards use, so a component
// can hide or disable an affordance without duplicating the rule.

/** The caller's role, from whichever slice has hydrated. */
export function useRole() {
  return useSelector(state =>
    state?.loggedinUser?.userInfo?.data?.data?.role
    || state?.userLogin?.userInfo?.data?.user?.role
    || null);
}

/**
 * May the current role perform `action` on `resource`?
 *
 * Mirrors backend/config/rbac.js. Returns false while the role is still
 * unresolved — an affordance should appear once we know it is allowed, not
 * before.
 */
export function usePermission(resource, action) {
  const role = useRole();
  if (!role) return false;
  return hasPermission(role, resource, action);
}

/**
 * Is `featureKey` enabled for this tenant?
 *
 * Returns false until the tenant config resolves, for the same reason: an
 * unresolved feature is not an enabled one.
 */
export function useFeature(featureKey) {
  return useSelector(state => {
    const features = state?.tenantConfig?.config?.features;
    if (!features || !featureKey) return false;
    return features[featureKey] === true;
  });
}

const access = { useRole, usePermission, useFeature };
export default access;
